/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';

import { Heading, IconBellOutline, MenuItem, Spacer, Text } from '@zillow/constellation';

import AppStoreModule from '../../AppStoreModule';

import type { NotificationItem } from './interfaces/notificationItem';

import NotificationMenuItem from '../NotificationMenuItem';
import api from './api';
import { ZRMUtils } from '../../utils';
import urlUtils from './util/urlUtils';
import Constants from './constants';

import DropdownMenu from '../DropdownMenu';

import {
  StyledDesktopDisplay,
  StyledIconBellOutlineContainer,
  StyledIconContainer,
  StyledMenuContentBody,
  StyledMenuContentFooter,
  StyledMobileDisplay,
  StyledModalDialog,
  StyledNotificationCircle,
  StyledNoUnderlineTextButton,
} from './style';

import type { InstanceAppStore } from '../../interfaces/appStore';

interface Props {
  proxyPath: string;
  csrf_token: string;
  brand: string;
  webSocketInstance?: unknown;
  zgApi: string;
  isLayoutHorizontal?: boolean;
  isOpen?: boolean;
  setNotificationStateValues?: React.Dispatch<
    React.SetStateAction<{
      notifications: Array<NotificationItem>;
      isPopupOpen: boolean;
      hasError: boolean;
      startIndex: number;
      showPagination: boolean;
      showMarkAllAsRead: boolean;
      isFirstTimeLoad: boolean;
      hasNewNotifications: boolean;
    }>
  >;
}

const NotificationCenter = ({
  proxyPath,
  csrf_token,
  brand,
  webSocketInstance,
  zgApi,
  isLayoutHorizontal = false,
  isOpen = false,
  setNotificationStateValues = () => {},
}: Props) => {
  const [stateValues, setStateValues] = useState<{
    notifications: Array<NotificationItem>;
    isPopupOpen: boolean;
    hasError: boolean;
    startIndex: number;
    showPagination: boolean;
    showMarkAllAsRead: boolean;
    isFirstTimeLoad: boolean;
    hasNewNotifications: boolean;
  }>({
    notifications: [],
    isPopupOpen: false,
    hasError: false,
    startIndex: 0,
    showPagination: false,
    showMarkAllAsRead: false,
    isFirstTimeLoad: true,
    hasNewNotifications: false,
  });
  const [showModal, setShowModal] = useState(false);
  const [ws, setWS] = useState(null);
  const [instanceAppStore, setInstanceAppStore] = useState<boolean | unknown | null>(null);

  const { isFirstTimeLoad, notifications, showPagination, hasError, showMarkAllAsRead, hasNewNotifications } =
    stateValues;

  useEffect(() => {
    asyncHasAppStoreInstance((response: unknown) => {
      if (response) {
        setInstanceAppStore(response);
      } else {
        setInstanceAppStore(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (instanceAppStore === null) {
      return;
    }

    // instance was not captured
    if (!instanceAppStore) {
      setupWSConnection();

      return;
    }

    const {
      appConfig: {
        features: { disableWebSocketRegistration = false },
      },
    } = (instanceAppStore || {}) as InstanceAppStore;

    if (!disableWebSocketRegistration) {
      setupWSConnection();
    }

    return () => {
      removeWSListeners();
      closeWS();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanceAppStore]);

  useEffect(() => {
    if (!ws) {
      removeWSListeners();
      closeWS();

      return;
    }

    registerWSListeners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  useEffect(() => {
    if (isFirstTimeLoad) {
      getAllNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFirstTimeLoad]);

  useEffect(() => {
    if (stateValues !== undefined) {
      // sync with parent component
      setNotificationStateValues({
        ...stateValues,
      });
    }
  }, [stateValues]);

  const closeBtnRef = useRef<HTMLButtonElement>(null);

  const getInstance = () => {
    return AppStoreModule.getInstance();
  };

  const asyncHasAppStoreInstance = (callback: (arg0: unknown) => unknown) => {
    ZRMUtils.recursiveDetection(
      {
        delay: 250,
        maxTries: 4,
        detectFunc: getInstance,
      },
      (response: unknown) => callback(response),
    );

    return;
  };

  const getAllNotifications = (isPagination = false, includeFlagForNewNotifications = true) => {
    const {
      isFirstTimeLoad: firstTimeLoad,
      showMarkAllAsRead: showMarkAll,
      notifications: notificationsFromState,
      startIndex: startIndexFromState,
    } = stateValues;

    let startIndex = firstTimeLoad ? 0 : startIndexFromState;
    let limit = 10;

    if (!isPagination) {
      limit = firstTimeLoad ? 10 : startIndexFromState;
      startIndex = 0;
    }

    api
      .getAllNotifications(proxyPath, csrf_token, startIndex, limit, includeFlagForNewNotifications, zgApi)
      .then(({ response }) => {
        const { notifications: responseNotifications, hasNewNotificationsForNotificationCenter = false } = response;
        let nextStartIndex = startIndexFromState;

        if (isPagination || firstTimeLoad) {
          nextStartIndex = Number(startIndex) + 10;
        }

        const { currentPage, numPages } = response.pager;
        const showPaginationBool = currentPage !== numPages;
        let showMarkAllAsReadBool = showMarkAll || chechIfUnreadNotifications(responseNotifications);
        if (!responseNotifications.length) {
          showMarkAllAsReadBool = false;
        }

        if (isPagination) {
          const newNotifications = [...notificationsFromState, ...responseNotifications];
          setStateValues({
            ...stateValues,
            notifications: newNotifications,
            startIndex: nextStartIndex,
            showPagination: showPaginationBool,
            showMarkAllAsRead: showMarkAllAsReadBool,
            isFirstTimeLoad: false,
            hasNewNotifications: hasNewNotificationsForNotificationCenter,
          });
        } else {
          setStateValues({
            ...stateValues,
            notifications: responseNotifications,
            startIndex: nextStartIndex,
            showPagination: showPaginationBool,
            showMarkAllAsRead: showMarkAllAsReadBool,
            isFirstTimeLoad: false,
            hasNewNotifications: hasNewNotificationsForNotificationCenter,
          });
        }

        return;
      })
      .catch(() => {
        setStateValues({
          ...stateValues,
          hasError: true,
          showPagination: false,
        });
      });
  };

  const setupWSConnection = () => {
    let newWS = null;

    // if webSocketInstance is passed use it. otherwise create new instance
    if (!webSocketInstance) {
      // @ts-ignore
      newWS = new WebSocket(`${Constants.WS_BRAND_CONNECTIONS[brand]}`);
    } else {
      // @ts-ignore
      newWS = webSocketInstance.subscriptionClient ? webSocketInstance.subscriptionClient.client : webSocketInstance;
    }

    console.log('setup websocket', newWS);
    setWS(newWS);
  };

  const registerWSListeners = () => {
    if (!ws) {
      return;
    }

    // @ts-ignore
    ws.addEventListener('message', handleWSMessage);
    // @ts-ignore
    ws.addEventListener('close', handleWSClose);
    // @ts-ignore
    ws.addEventListener('error', handleWSError);
  };

  const removeWSListeners = () => {
    if (!ws) {
      return;
    }

    // @ts-ignore
    ws.removeEventListener('message', handleWSMessage);
    // @ts-ignore
    ws.removeEventListener('close', handleWSClose);
    // @ts-ignore
    ws.removeEventListener('error', handleWSError);
  };

  const closeWS = () => {
    if (!ws) {
      return;
    }

    // @ts-ignore
    ws.close();
  };

  const handleMenuItemClick = (notification: NotificationItem) => {
    const url = urlUtils.getUrl(notification);
    handleMarkRead(notification);
    closeDropdown();

    window.open(url, '_blank');
  };

  const chechIfUnreadNotifications = (notificationsFromState: Array<NotificationItem>) => {
    return notificationsFromState.filter((notification) => notification.status === 'UNREAD').length > 0;
  };

  const closeDropdown = () => {
    closeBtnRef?.current?.click();
  };

  const handleWSMessage = (event: unknown) => {
    console.log('>> websocket message received.', event);
    getAllNotifications();
  };

  const handleWSClose = (event: unknown) => {
    console.log('>> websocket closed successfully.', event);
    setWS(null);
  };

  const handleWSError = (event: unknown) => {
    console.log('>> websocket connection failed', event);
  };

  const handleNotificationsExpand = () => {
    api
      .updateNotificationreadTimestamp(proxyPath, csrf_token, zgApi)
      .then(() => {
        setStateValues({
          ...stateValues,
          hasNewNotifications: false,
        });

        return;
      })
      .catch(() => console.error('ERROR: updating notification read timestamp'));
  };

  const handleMarkRead = ({ notificationId }: NotificationItem) => {
    api
      .markAsRead(proxyPath, csrf_token, notificationId, zgApi)
      .then(() => {
        const updatedNotifications = notifications.map((notificationFromState: NotificationItem) => {
          if (notificationId === notificationFromState.notificationId) {
            notificationFromState.status = 'READ';
          }

          return notificationFromState;
        });

        setStateValues({ ...stateValues, notifications: updatedNotifications });

        return;
      })
      .catch(() => {
        // TODO: add error handling for individual item in MUI to support this
        // eslint-disable-next-line no-console
        console.log('could not mark as as read');
      });
  };

  const handleMarkAllAsRead = () => {
    api
      .markAllAsRead(proxyPath, csrf_token, undefined, zgApi)
      .then(() => {
        const updatedNotifications = notifications.map((notification) => {
          notification.status = 'READ';
          return notification;
        });

        setStateValues({
          ...stateValues,
          hasNewNotifications: false,
          notifications: updatedNotifications,
          showMarkAllAsRead: false,
        });

        return;
      })
      .catch(() => {
        // TODO: add error handling for mark all as read in MUI to support this
        // eslint-disable-next-line no-console
        console.log('could not mark all as read');
      });
  };

  const handleOpen = () => {
    handleNotificationsExpand();
  };

  const getMoreNotifications = (event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
    if (event) {
      event.stopPropagation();
    }

    const isPagination = true;
    getAllNotifications(isPagination);
  };

  const renderNotifications = () => {
    if (notifications.length === 0) {
      return (
        <MenuItem>
          <Text as="p" fontType="bodySmall">
            You do not have any notifications
          </Text>
        </MenuItem>
      );
    }

    return notifications.map((notificationItem: NotificationItem) => (
      <NotificationMenuItem
        key={`notificationItem-${notificationItem.notificationId}`}
        notificationItem={notificationItem}
        onClick={() => handleMenuItemClick(notificationItem)}
      />
    ));
  };

  const renderIcon = () => {
    if (isLayoutHorizontal === false) {
      return (
        <StyledIconContainer data-popper-placement="bottom" aria-label="View notifications">
          {hasNewNotifications ? <StyledNotificationCircle /> : null}
          <IconBellOutline size="sm" />
        </StyledIconContainer>
      );
    }

    // horizontal layout
    return (
      <StyledIconContainer data-popper-placement="bottom" aria-label="View notifications" tabIndex={-1}>
        <StyledIconBellOutlineContainer>
          <IconBellOutline size="sm" />
        </StyledIconBellOutlineContainer>
      </StyledIconContainer>
    );
  };

  const renderContent = ({ disableMaxHeight }: { disableMaxHeight: boolean }) => (
    <StyledMenuContentBody className="StyledMenuContentBody" disableMaxHeight={disableMaxHeight}>
      {renderNotifications()}
      {showPagination && (
        <MenuItem onClick={getMoreNotifications} data-auto-close={false}>
          <StyledNoUnderlineTextButton fontType="finePrint">Show more notifications</StyledNoUnderlineTextButton>
        </MenuItem>
      )}
    </StyledMenuContentBody>
  );

  const renderError = () => (
    <MenuItem>
      <Text fontType="body">There was an error in fetching the notifications</Text>
    </MenuItem>
  );

  return (
    <div data-test="NotificationCenter">
      <StyledDesktopDisplay>
        <DropdownMenu title={renderIcon()} onOpen={handleOpen} isLayoutHorizontal={isLayoutHorizontal} pIsOpen={isOpen}>
          <React.Fragment>
            {hasError ? (
              renderError()
            ) : (
              <React.Fragment>
                {renderContent({ disableMaxHeight: false })}
                {showMarkAllAsRead && (
                  <StyledMenuContentFooter className="StyledMenuContentFooter">
                    <MenuItem onClick={handleMarkAllAsRead}>
                      <Spacer marginY="xs">
                        <StyledNoUnderlineTextButton fontType="bodyHeading">
                          Mark all as read
                        </StyledNoUnderlineTextButton>
                      </Spacer>
                    </MenuItem>
                  </StyledMenuContentFooter>
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        </DropdownMenu>
      </StyledDesktopDisplay>
      <StyledMobileDisplay>
        <StyledIconContainer
          aria-label="View notifications"
          icon={
            <>
              {hasNewNotifications ? <StyledNotificationCircle /> : null}
              <IconBellOutline size="sm" />
            </>
          }
          onClick={() => setShowModal(true)}
        />
        {showModal && (
          <StyledModalDialog
            isOpen={showModal}
            header={<Heading level={5}>Notifications</Heading>}
            onClose={() => setShowModal(false)}
            fullScreen={true}
            body={hasError ? renderError() : renderContent({ disableMaxHeight: true })}
            footer={
              showMarkAllAsRead && (
                <MenuItem onClick={handleMarkAllAsRead}>
                  <Spacer marginY="xs">
                    <StyledNoUnderlineTextButton fontType="bodyHeading">Mark all as read</StyledNoUnderlineTextButton>
                  </Spacer>
                </MenuItem>
              )
            }
          />
        )}
      </StyledMobileDisplay>
    </div>
  );
};

NotificationCenter.defaultProps = {
  name: 'NotificationCenter',
};

export default NotificationCenter;
